import React, { useState } from 'react';

import { useGetEvent } from '@vlabs/api/artemy/client';
import type { EventId } from '@vlabs/api/artemy/model/EventId';
import { useHasPermission, usePageAccess } from '@vlabs/permissions';
import { ButtonSwitch, ButtonSwitchGroup } from '@vlabs/uikit';
import type { WithDataTestid, WithClassNameProp } from '@vlabs/uikit/types';
import cn from 'classnames';

import { FindSimilarDropdownPoint3 } from '@vlabs/monokit/features/point/find-similar';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { AttachFaceToCardButtonPoint3 } from '@vlabs/monokit/widgets/attach-face-to-card-button/@x/event-details';

import st from './EventDetection.module.sass';
import { useEventDetectionTranslation } from './i18n';
import { EventDetectionAttributesPoint3 } from './tabs/EventDetectionAttributesPoint3';
import { EventDetectionPropertiesPoint3 } from './tabs/EventDetectionPropertiesPoint3';

type StatsType = 'attributes' | 'properties';
interface EventDetectionPoint3Props extends WithDataTestid, WithClassNameProp {
  eventId: EventId;
}

const displayName = 'EventDetectionPoint3';
const testid = buildTestid(displayName);

function EventDetectionPoint3({
  eventId,
  className = undefined,
  'data-testid': dataTestId = undefined,
}: EventDetectionPoint3Props): React.JSX.Element {
  const { t } = useEventDetectionTranslation();

  const [statsType, setStatsType] = useState<StatsType>('attributes');
  const canAttachFaceToCard = useHasPermission('card', 'create');
  const canSearch = usePageAccess('search');

  const { data: event } = useGetEvent(eventId, { target: ['face_detections'] });
  const faceDetection = event?.face_detections?.find(({ sample_id }) => !!sample_id);

  return (
    <div
      className={cn(st.Root, className)}
      {...testid(dataTestId)}
    >
      <div className={st.Heading}>
        <ButtonSwitchGroup
          onChange={setStatsType}
          size="small"
          value={statsType}
          variant="square"
          {...testid(dataTestId)}
        >
          <ButtonSwitch value="attributes">
            {t('attributes')}
          </ButtonSwitch>

          <ButtonSwitch value="properties">
            {t('additional_properties')}
          </ButtonSwitch>
        </ButtonSwitchGroup>

        <div className={st.Actions}>
          {!!canAttachFaceToCard && !!faceDetection?.sample_id && (
            <AttachFaceToCardButtonPoint3
              sampleId={faceDetection.sample_id}
              size="small"
              {...testid(dataTestId)}
            />
          )}
          {!!canSearch && (
            <FindSimilarDropdownPoint3
              eventId={eventId}
              type="detection"
              {...testid(dataTestId)}
            />
          )}
        </div>
      </div>
      <div className={st.Content}>
        {statsType === 'attributes' && <EventDetectionAttributesPoint3 eventId={eventId} />}
        {statsType === 'properties' && <EventDetectionPropertiesPoint3 eventId={eventId} />}
      </div>
    </div>
  );
}

EventDetectionPoint3.displayName = displayName;

export { EventDetectionPoint3 };
